import { CircularProgress } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { NavigateListener } from 'components';
import { AppHistoryProvider } from 'context';
import { AppConfigArea, AppConfigKey } from 'enums';
import { useAppConfigValue, useSessionRestore, useTheme } from 'hooks';

import { AppRoutes } from './AppRoutes';
import './App.scss';
import createTheme from './theme';

const ThemeLoader: React.FC = () => {
  const { themeName, initThemes } = useTheme();
  const themeQueryResult = useAppConfigValue({
    area: AppConfigArea.Appearance,
    key: AppConfigKey.ThemeOverrides,
    isPublic: true,
  });

  useEffect(() => {
    if (!isEmpty(themeQueryResult.data)) {
      initThemes(themeQueryResult.data as any);
    }
  }, [themeQueryResult.data, initThemes, themeName]);

  return null;
};

const AppContent: React.FC = () => {
  const isRestoringSession = useSessionRestore();

  if (isRestoringSession) {
    return (
      <div className="tw-text-center tw-my-8">
        <CircularProgress />
      </div>
    );
  }

  return <AppRoutes />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { themeName, customThemeConfig } = useTheme();

  return (
    <MuiThemeProvider theme={createTheme(themeName, customThemeConfig)}>
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <BrowserRouter>
            <AppHistoryProvider>
              <NavigateListener />
              <AppContent />
              <ThemeLoader />
              <ToastContainer
                position="top-center"
                hideProgressBar
                autoClose={3000}
              />
            </AppHistoryProvider>
          </BrowserRouter>
        </LocalizationProvider>
      </QueryClientProvider>
    </MuiThemeProvider>
  );
}

export default App;
